import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const STAR_COUNT = 60;

/**
 * Fixed-position twinkling starfield, dark mode only. Mounted once behind
 * content (Login's hero, and the authenticated app shell) -- callers must
 * give their own content `position: relative; zIndex: 1` to stack above it.
 */
function Starfield() {
  const theme = useTheme();

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }, []);

  const stars = useMemo(
    () =>
      Array.from({ length: STAR_COUNT }, () => ({
        top: `${(Math.random() * 100).toFixed(2)}%`,
        left: `${(Math.random() * 100).toFixed(2)}%`,
        size: Math.random() < 0.8 ? 1 : Math.random() < 0.6 ? 1.5 : 2.5,
        duration: 2.5 + Math.random() * 3.5,
        // negative delay starts each star mid-cycle at a random phase, so
        // they read as desynchronized from the very first frame
        delay: -(Math.random() * 6),
      })),
    []
  );

  if (theme.palette.mode !== "dark") return null;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
        "@keyframes starTwinkle": {
          "0%, 100%": { opacity: 0.15 },
          "50%": { opacity: 0.9 },
        },
      }}
    >
      {stars.map((star, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: star.top,
            left: star.left,
            // explicit "px" strings -- MUI's sx width/height treats bare
            // numbers in [0,1] as fractional percentages (1 -> "100%"), and
            // 1 was this field's most common star size
            width: `${star.size}px`,
            height: `${star.size}px`,
            borderRadius: "50%",
            backgroundColor: "#FFFFFF",
            opacity: prefersReducedMotion ? 0.4 : undefined,
            animation: prefersReducedMotion
              ? "none"
              : `starTwinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

export default Starfield;
